import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import BadgeListControl from "../OrganizationConfiguration/BadgeListControl";
import RuleCaseSearch from "./RuleCaseSearch";
import { CasesRepository } from "../../repositories/CasesRepository";
import { ConflictReason } from "../../models/ConflictReason";
import { isGuid, plural } from "../../helpers/CommonHelpers";

// New lines, commas, semicolons and tabs separate entries; spaces do not, because a case named after its folder can
// hold one.
const SEPARATOR = /[,;\t\n\r]+/;
// The server's longest condition value, which a listed case number is checked against as well.
const MAX_ENTRY_LENGTH = 260;
// One request per this many case ids; each id adds about 48 characters to the address.
const IDS_PER_LOOKUP = 100;
const TOAST_LIST_LIMIT = 5;

/** The entries without repeats, ignoring letter case as the server matches them, each kept as first written. */
export const withoutRepeats = (entries: string[]): string[] => {
  const seen = new Set<string>();
  return entries.filter((entry) => {
    const key = entry.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const validateEntry = (input: string): string | null => {
  const entry = input.trim();
  return entry.length === 0 || entry.length > MAX_ENTRY_LENGTH ? null : entry;
};

interface RuleCaseListControlProps {
  label: string;
  values: string[];
  /**
   * Changes the list from its latest value. A case-id lookup finishes after the paste that started it, so it must not
   * overwrite edits made meanwhile.
   */
  onChange: (update: (current: string[]) => string[]) => void;
  /** The rule's conflict types, for "Find cases". */
  reasons: ConflictReason[];
  disabled?: boolean;
}

/**
 * The rule's case list: entries pasted or typed many at a time, or picked with "Find cases". A pasted case id (the
 * GUID in a case's address or the Case ID column) is replaced by its case number, so the stored rule names cases the
 * way people read them and stays meaningful in the shared library.
 */
const RuleCaseListControl: React.FC<RuleCaseListControlProps> = ({ label, values, onChange, reasons, disabled }) => {
  const repository = new CasesRepository();
  const [idsLookingUp, setIdsLookingUp] = useState(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const resolveIds = async (ids: string[]) => {
    setIdsLookingUp((count) => count + ids.length);
    const numbers = new Map<string, string>();
    let failed = false;
    try {
      for (let start = 0; start < ids.length; start += IDS_PER_LOOKUP) {
        const chunk = ids.slice(start, start + IDS_PER_LOOKUP);
        const { data } = await repository.getCases({ ids: chunk, pageNumber: 1, pageSize: chunk.length });
        data.forEach((found) => numbers.set(found.id.toLowerCase(), found.caseNumber));
      }
    } catch {
      failed = true;
    }
    if (!mounted.current) return;
    setIdsLookingUp((count) => count - ids.length);

    // An id the lookup never reached stays as typed; one it reached and did not find is not a case here.
    const pending = new Set(ids.map((id) => id.toLowerCase()));
    if (numbers.size > 0 || !failed)
      onChange((current) =>
        withoutRepeats(
          current.flatMap((entry) => {
            const key = entry.toLowerCase();
            if (!pending.has(key)) return [entry];
            const caseNumber = numbers.get(key);
            return caseNumber ? [caseNumber] : failed ? [entry] : [];
          })
        )
      );

    if (numbers.size > 0) toast.success(`Replaced ${plural(numbers.size, "case id")} with case numbers.`);
    if (failed) {
      toast.error("Some case ids could not be looked up and stay in the list as typed. Remove them, then paste them again.");
      return;
    }
    const missing = ids.filter((id) => !numbers.has(id.toLowerCase()));
    if (missing.length > 0) {
      const shown = missing.slice(0, TOAST_LIST_LIMIT).join(", ");
      const more = missing.length > TOAST_LIST_LIMIT ? ` and ${missing.length - TOAST_LIST_LIMIT} more` : "";
      toast.warning(`Not a case in this organization, so left out: ${shown}${more}.`);
    }
  };

  const handleChange = (next: string[]) => {
    const unique = withoutRepeats(next);
    const known = new Set(values.map((entry) => entry.toLowerCase()));
    onChange(() => unique);
    const newIds = unique.filter((entry) => isGuid(entry) && !known.has(entry.toLowerCase()));
    if (newIds.length > 0) void resolveIds(newIds);
  };

  const longest = values.reduce((max, entry) => Math.max(max, entry.length), 0);

  return (
    <div className="flex flex-col gap-1">
      <BadgeListControl
        label={label}
        labelClassName="text-xs font-normal text-muted-foreground"
        values={values}
        onChange={handleChange}
        validateEntry={validateEntry}
        separator={SEPARATOR}
        disabled={disabled}
        importable={false}
        // Wide enough for a TW- number; folder-named cases get up to the chip's own maximum.
        chipSlotWidth={Math.min(184, Math.max(96, longest * 7 + 40))}
        addPlaceholder="Paste or type case numbers or case ids, then press Enter…"
        tooltipText="Separate entries with new lines, commas, semicolons or tabs. A case id is replaced by its case number."
        entityLabel="case numbers"
        listName="the case list"
        inputAddon={
          <RuleCaseSearch
            listed={values}
            reasons={reasons}
            disabled={disabled}
            onAdd={(caseNumbers) => onChange((current) => withoutRepeats([...current, ...caseNumbers]))}
            onRemove={(caseNumber) =>
              onChange((current) => current.filter((entry) => entry.toLowerCase() !== caseNumber.toLowerCase()))
            }
          />
        }
      />
      {idsLookingUp > 0 && (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          Looking up {plural(idsLookingUp, "case id")}…
        </span>
      )}
    </div>
  );
};

export default RuleCaseListControl;
